import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  CheckCircle,
  Search,
  Eye,
  Plus,
  MoreHorizontal,
  Filter,
  Download,
  RefreshCw,
  Calculator,
  Receipt
} from 'lucide-react';
import expenseService from '@/services/expenseService';
import {
  Expense,
  ExpenseCategory,
  EXPENSE_CATEGORY_INFO
} from '@/types/expense';

interface ApprovedExpensesForInvoicingProps {
  contractId?: string; // Filter by specific contract
  onCreateInvoice?: (expenses: Expense[]) => void;
  className?: string;
}

const ApprovedExpensesForInvoicing: React.FC<ApprovedExpensesForInvoicingProps> = ({
  contractId,
  onCreateInvoice,
  className
}) => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [selectedExpenses, setSelectedExpenses] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ExpenseCategory | ''>('');
  const { toast } = useToast();

  const fetchApprovedExpenses = async () => {
    if (!contractId) {
      setExpenses([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      const response = await expenseService.getApprovedExpensesForInvoicing(contractId);
      let filteredExpenses = response.expenses || [];
      
      // Apply frontend filters
      if (searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        filteredExpenses = filteredExpenses.filter((expense: Expense) =>
          expense.description.toLowerCase().includes(query) ||
          expense.id.toLowerCase().includes(query)
        );
      }
      
      if (selectedCategory) {
        filteredExpenses = filteredExpenses.filter((expense: Expense) =>
          expense.category === selectedCategory
        );
      }
      
      setExpenses(filteredExpenses);
    } catch (error: any) {
      console.error('Error fetching approved expenses:', error);
      toast({
        title: 'Error',
        description: 'Failed to load approved expenses',
        variant: 'destructive',
      });
      setExpenses([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchApprovedExpenses();
  }, [contractId, searchQuery, selectedCategory]);

  const handleSelectExpense = (expenseId: string) => {
    const newSelected = new Set(selectedExpenses);
    if (newSelected.has(expenseId)) {
      newSelected.delete(expenseId);
    } else {
      newSelected.add(expenseId);
    }
    setSelectedExpenses(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedExpenses.size === expenses.length) {
      setSelectedExpenses(new Set());
    } else {
      setSelectedExpenses(new Set(expenses.map(e => e.id)));
    }
  };

  const handleCreateInvoice = () => {
    const expensesToInvoice = expenses.filter(e => selectedExpenses.has(e.id));
    if (expensesToInvoice.length === 0) {
      toast({
        title: 'No expenses selected',
        description: 'Please select at least one expense to create an invoice',
        variant: 'destructive',
      });
      return;
    }
    
    onCreateInvoice?.(expensesToInvoice);
  };

  const handleViewReceipt = async (expense: Expense) => {
    try {
      await expenseService.downloadReceipt(expense.id);
    } catch (error: any) {
      console.error('Error viewing receipt:', error);
      toast({
        title: 'Error',
        description: 'Failed to view receipt. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const formatAmount = (amount: string) => {
    return expenseService.formatAmount(amount);
  };

  const formatDate = (dateString: string) => {
    return expenseService.formatDate(dateString);
  };

  const getTotalSelectedAmount = () => {
    return expenses
      .filter(e => selectedExpenses.has(e.id))
      .reduce((total, expense) => total + parseFloat(expense.amount), 0);
  };

  const getCategoryInfo = (category: ExpenseCategory) => {
    return EXPENSE_CATEGORY_INFO[category];
  };

  const allSelected = expenses.length > 0 && 
    expenses.every(expense => selectedExpenses.has(expense.id));
  const someSelected = expenses.some(expense => selectedExpenses.has(expense.id));

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Ready for Invoicing
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between p-4 border rounded-md">
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                  <div className="h-3 bg-muted rounded animate-pulse w-1/2" />
                </div>
                <div className="h-8 w-20 bg-muted rounded animate-pulse" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <CardTitle>Ready for Invoicing</CardTitle>
            </div>
            {expenses.length > 0 && (
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                {expenses.length} ready
              </Badge>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={fetchApprovedExpenses}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {expenses.length > 0 && (
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div>
              Total value: {formatAmount(getTotalSelectedAmount().toString())}
            </div>
            {selectedExpenses.size > 0 && (
              <div className="font-medium text-primary">
                {selectedExpenses.size} selected ({formatAmount(getTotalSelectedAmount().toString())})
              </div>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Search and Filters */}
        <div className="flex gap-2 flex-col sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search expenses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Filter className="h-4 w-4 mr-2" />
                {selectedCategory ? getCategoryInfo(selectedCategory).label : 'All Categories'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => setSelectedCategory('')}>
                All Categories
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {Object.values(ExpenseCategory).map((category) => {
                const categoryInfo = getCategoryInfo(category);
                return (
                  <DropdownMenuItem
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                  >
                    <span className="mr-2">{categoryInfo.icon}</span>
                    {categoryInfo.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Selection Actions */}
        {selectedExpenses.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-md">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calculator className="h-5 w-5 text-blue-600" />
                <div>
                  <div className="font-medium text-blue-800">
                    {selectedExpenses.size} expenses selected
                  </div>
                  <div className="text-sm text-blue-600">
                    Total: {formatAmount(getTotalSelectedAmount().toString())}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                {onCreateInvoice && (
                  <Button
                    onClick={handleCreateInvoice}
                    size="sm"
                    className="gap-2"
                  >
                    <Receipt className="h-4 w-4" />
                    Create Invoice
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Expenses Table */}
        {expenses.length > 0 ? (
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={handleSelectAll}
                      className={someSelected && !allSelected ? 'indeterminate' : ''}
                    />
                  </TableHead>
                  <TableHead>Expense</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Approved By</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((expense) => {
                  const categoryInfo = getCategoryInfo(expense.category);
                  const isSelected = selectedExpenses.has(expense.id);
                  
                  return (
                    <TableRow 
                      key={expense.id} 
                      className={`group ${isSelected ? 'bg-blue-50' : ''}`}
                    >
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleSelectExpense(expense.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">{expense.description}</div>
                          <div className="text-sm text-muted-foreground">
                            ID: {expense.id}
                          </div>
                          {expense.reviewedAt && (
                            <div className="text-xs text-muted-foreground">
                              Approved {formatDate(expense.reviewedAt)}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span>{categoryInfo.icon}</span>
                          <span className="text-sm">{categoryInfo.label}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {formatDate(expense.expenseDate)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {formatAmount(expense.amount)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {expense.reviewedByEmail || 'Unknown'}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <TooltipProvider>

                            
                            {expense.receiptUrl && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleViewReceipt(expense)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>View receipt</p>
                                </TooltipContent>
                              </Tooltip>
                            )}

                            <DropdownMenu>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>More actions</p>
                                </TooltipContent>
                              </Tooltip>
                              <DropdownMenuContent align="end">

                                {onCreateInvoice && (
                                  <DropdownMenuItem 
                                    onClick={() => onCreateInvoice([expense])}
                                  >
                                    <Receipt className="mr-2 h-4 w-4" />
                                    Create Invoice
                                  </DropdownMenuItem>
                                )}
                                {expense.receiptUrl && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleViewReceipt(expense)}>
                                      <Download className="mr-2 h-4 w-4" />
                                      View Receipt
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
              {expenses.length === 0 && expenses.length > 0 ? (
                <Search className="h-6 w-6 text-green-600" />
              ) : (
                <CheckCircle className="h-6 w-6 text-green-600" />
              )}
            </div>
            <h3 className="text-lg font-medium mb-1">
              {expenses.length === 0 && expenses.length > 0 
                ? 'No matching expenses'
                : 'No expenses ready for invoicing'
              }
            </h3>
            <p className="text-muted-foreground mb-4">
              {expenses.length === 0 && expenses.length > 0
                ? 'Try adjusting your search or filter criteria'
                : 'Approved expenses will appear here and can be included in invoices'
              }
            </p>
            {expenses.length === 0 && expenses.length > 0 && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('');
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        )}

        {/* Summary Info */}
        {expenses.length > 0 && (
          <div className="bg-muted/50 p-4 rounded-md">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div className="text-center">
                <div className="font-medium">{expenses.length}</div>
                <div className="text-muted-foreground">Total Expenses</div>
              </div>
              <div className="text-center">
                <div className="font-medium">{formatAmount(getTotalSelectedAmount().toString())}</div>
                <div className="text-muted-foreground">Total Value</div>
              </div>
              <div className="text-center">
                <div className="font-medium">{selectedExpenses.size}</div>
                <div className="text-muted-foreground">Selected</div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ApprovedExpensesForInvoicing; 